import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Card,
  CardContent,
  Stack,
  IconButton,
  Snackbar,
  Alert,
  InputAdornment,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PlaceIcon from '@mui/icons-material/Place';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LandingPageNav from '../../components/landingpage/LandingPageNav';
import Footer from '../../components/Footer/footer';
import { apipostPublic } from '../../service/api'; // ⬅️ use backend helper

const STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','District of Columbia','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'
];

// Dropdown options
const TOPICS = [
  'New Mortgages',
  'My Account Login Assistance',
  'Payment Questions',
  'Careers',
  'Privacy Policy Questions',
  'General Questions',
  'JV Partnership', // new
];

export default function ContactUsPage() {
  const [form, setForm] = useState({
    topic: '',
    name: '',
    email: '',
    phone: '',
    company: '',
    state: '',
    address: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Show address only for these topics
  const showAddress =
    form.topic === 'New Mortgages' || form.topic === 'Payment Questions';

  // Require ONLY: name, email, phone, state
  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Enter a valid email';
    if (!form.phone.trim()) newErrors.phone = 'Phone is required';
    if (!form.state) newErrors.state = 'Select a state';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length) return;

    try {
      setLoading(true);
      // Send everything, backend can ignore blanks/conditionals
      const payload = { ...form, address: showAddress ? form.address : '' };

      // Use your shared API helper
      const res = await apipostPublic('contactus/add', payload);
      if (!res || res.status >= 400) throw new Error('Request failed');

      setSnack({ open: true, severity: 'success', message: 'Thanks! Your message has been sent.' });
      setForm({
        topic: '',
        name: '',
        email: '',
        phone: '',
        company: '',
        state: '',
        address: '',
        message: '',
      });
    } catch (err) {
      setSnack({ open: true, severity: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LandingPageNav />
      <Container maxWidth="md" sx={{ pt: { xs: 10, md: 12 }, pb: { xs: 6, md: 10 } }}>
        <Card elevation={6} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
              <Grid item xs={12} md="auto">
                <Typography variant="h4" sx={{ fontWeight: 800 }}>Contact Us</Typography>
              </Grid>
              <Grid item xs={12} md="auto">
                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  <IconButton component="a" href="https://facebook.com/JetHomeLoans" target="_blank" rel="noopener" aria-label="Facebook"><FacebookIcon /></IconButton>
                  <IconButton component="a" href="https://www.instagram.com/jethomeloans/" target="_blank" rel="noopener" aria-label="Instagram"><InstagramIcon /></IconButton>
                  <IconButton
  component="a"
  href="https://x.com/JetHomeLoans"
  target="_blank"
  rel="noopener"
  aria-label="X"
>
  <img
    src="https://res.cloudinary.com/ddhrdxc7d/image/upload/v1763050149/X_Social_Media_Icon_niizkk.png"
    alt="X"
    style={{
      width: 24,
      height: 24,
      display: 'block',
        filter: 'brightness(0)', 
    }}
  />
</IconButton>
<IconButton component="a" href="https://www.linkedin.com/company/jet-homeloans/" target="_blank" rel="noopener" aria-label="LinkedIn"><LinkedInIcon /></IconButton>
                </Stack>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={3} sx={{ mt: 2, mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneIcon fontSize="small" />
                <Typography>833-270-7191</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <EmailIcon fontSize="small" />
                <Typography>info@jethl.com</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <PlaceIcon fontSize="small" />
                <Typography>14701 Philips Highway, Suite 202 Jacksonville, FL 32256</Typography>
              </Stack>
            </Stack>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>
                {/* NEW: I have a question about (dropdown) */}
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="I have a question about"
                    name="topic"
                    value={form.topic}
                    onChange={onChange}
                  >
                    {TOPICS.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    error={Boolean(errors.phone)}
                    helperText={errors.phone}
                    InputProps={{ startAdornment: <InputAdornment position="start">+1</InputAdornment> }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="company"
                    value={form.company}
                    onChange={onChange}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    select
                    fullWidth
                    label="State"
                    name="state"
                    value={form.state}
                    onChange={onChange}
                    error={Boolean(errors.state)}
                    helperText={errors.state}
                  >
                    {STATES.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Conditionally render Property Address */}
                {showAddress && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Property Address"
                      name="address"
                      value={form.address}
                      onChange={onChange}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={5}
                    label="Message"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      bgcolor: '#b79a59',
                      '&:hover': { bgcolor: '#a88a4d' },
                      py: 1.5,
                      fontWeight: 700,
                      letterSpacing: 1,
                    }}
                  >
                    {loading ? 'SENDING…' : 'SEND'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Container>
      <Footer />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
